import { parseSpreadsheetNumericCellValue } from "@common/helpers/spreadsheet";
import { CellValue } from "@components/spreadsheet/types";
import { NativeInvoiceLineRow } from "./nativeInvoiceFormTypes";

export interface LineItemSpreadsheetRow {
    id: string;
    productOrService: string;
    description: string;
    quantity: number | null;
    unitAmount: number | null;
    amount: number;
    classIds: string[];
    accrualAccountId: string | null;
    receivablesAccountId: string | null;
    tax: number;
    discount: number;
    deposit: number;
}

type EditableLineField = Exclude<keyof LineItemSpreadsheetRow, "id" | "amount">;

function isEditableLineField(field: string): field is EditableLineField {
    return (
        field === "productOrService" ||
        field === "description" ||
        field === "quantity" ||
        field === "unitAmount" ||
        field === "classIds" ||
        field === "accrualAccountId" ||
        field === "receivablesAccountId" ||
        field === "tax" ||
        field === "discount" ||
        field === "deposit"
    );
}

function toNumericCellValue(
    value: CellValue | undefined,
): number | string | null | undefined {
    if (typeof value === "boolean" || Array.isArray(value)) {
        return null;
    }
    return value;
}

export function buildLinePatch(
    field: string,
    value: CellValue | undefined,
): Partial<NativeInvoiceLineRow> | null {
    if (!isEditableLineField(field)) {
        return null;
    }
    if (field === "quantity") {
        return {
            quantity: parseSpreadsheetNumericCellValue(
                toNumericCellValue(value),
            ),
        };
    }
    if (field === "unitAmount") {
        // Clearing the cell reverts to $0.00 (the initial state) rather than
        // leaving a blank rate.
        return {
            unitAmount:
                parseSpreadsheetNumericCellValue(toNumericCellValue(value)) ??
                0,
        };
    }
    if (field === "tax" || field === "discount" || field === "deposit") {
        return {
            [field]:
                parseSpreadsheetNumericCellValue(toNumericCellValue(value)) ??
                0,
        };
    }
    if (field === "productOrService") {
        // Manually renaming the item text detaches the catalog backlink so a
        // stored catalogItemId/catalogItemPriceId can't disagree with the displayed name.
        return {
            productOrService: value == null ? "" : String(value),
            catalogItemId: null,
            catalogItemPriceId: null,
        };
    }
    if (field === "classIds") {
        return {
            classIds: Array.isArray(value)
                ? value.filter(
                      (item): item is string => typeof item === "string",
                  )
                : [],
        };
    }
    if (field === "accrualAccountId") {
        return {
            accrualAccountId: value == null ? null : String(value),
        };
    }
    if (field === "receivablesAccountId") {
        return {
            receivablesAccountId: value == null ? null : String(value),
        };
    }
    if (field === "description") {
        return { description: value == null ? "" : String(value) };
    }
    // Exhaustiveness guard: every EditableLineField must have an explicit
    // branch above. If a new field is added to the type without a matching
    // branch, this fails to compile instead of silently storing the edit under
    // the `description` key.
    const exhaustive: never = field;
    return exhaustive;
}
